import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Settings card — shows the impersonation audit log so the security team
 * can review who impersonated whom + when it ended.
 *
 * Consumes GET /api/admin/impersonate/log
 */
export function ImpersonationAuditCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/impersonate/log");
      setRows(data?.items || data?.log || []);
    } catch (e) {
      setErr(apiErrorMessage(e) || "Could not load impersonation log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="impersonation-audit-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Security</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-orange-400" /> Impersonation audit
          </div>
          <div className="text-sm text-zinc-400 mt-1">Every admin-as-resident session, with start and end timestamps.</div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10" data-testid="impersonation-audit-refresh">
          <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      {err && <div className="text-xs text-red-300 mb-2" data-testid="impersonation-audit-error">{err}</div>}

      {!err && !loading && rows.length === 0 && (
        <div className="text-xs text-zinc-500 py-6 text-center" data-testid="impersonation-audit-empty">No impersonation events recorded yet.</div>
      )}

      <div className="space-y-1 max-h-72 overflow-y-auto scroll-slim">
        {rows.map((r, i) => {
          const kind = String(r.action || "impersonate.start");
          const isEnd = kind.endsWith(".end") || kind.includes("end");
          return (
            <div key={r.seq || r.id || i} className="flex items-center justify-between gap-3 text-xs border border-white/5 rounded-md px-3 py-2 bg-white/[0.02]" data-testid="impersonation-audit-row">
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className={isEnd ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-orange-500/40 text-orange-300 bg-orange-500/10"}>
                  {isEnd ? "end" : "start"}
                </Badge>
                <div className="truncate">
                  <span className="text-zinc-300">{r.actor_name || r.actor_id || "admin"}</span>
                  <span className="text-zinc-500"> → </span>
                  <span className="text-zinc-300">{r.resource_id || r.subject_id || "resident"}</span>
                  {r.action && <span className="text-[10px] text-zinc-500 ml-2">({r.action})</span>}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 shrink-0">{(r.at || r.created_at || "").slice(0, 19).replace("T", " ")}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
